// screens/HomeScreen.js - Production Home
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSync } from '../contexts/SyncContext';
import { useLang } from '../contexts/LanguageContext';
import { SyncBar } from '../components/shared';
import api from '../utils/api';
import { isTabletMode } from '../utils/tabletMode';
import { canViewComplaints } from '../utils/complaints';

// Rate-limit auto-warmup so we don't refetch reference data on every Home visit.
const WARMUP_RATE_LIMIT_MIN = 30;

export default function HomeScreen() {
  const { user } = useAuth();
  const { isOnline, lastWarmup, warmOfflineCache } = useSync();
  const navigate = useNavigate();
  const { t } = useLang();
  const [notifications, setNotifications] = useState({ unread: 0 });

  useEffect(() => {
    loadNotifications();
  }, []);

  // Warm the offline cache silently when the inspector opens Home with wifi.
  // Skips if we already warmed up within the last 30 min so we don't refetch on every visit.
  useEffect(() => {
    if (!isOnline) return;
    const stale = !lastWarmup || (Date.now() - lastWarmup.getTime()) > WARMUP_RATE_LIMIT_MIN * 60 * 1000;
    if (stale) warmOfflineCache();
  }, [isOnline, lastWarmup, warmOfflineCache]);

  const loadNotifications = async () => {
    try {
      const { data } = await api.get('/api/notifications');
      setNotifications(data);
    } catch {}
  };

  const tablet = isTabletMode();
  const isPhotographer = ['Photographer', 'R&D Photographer'].includes(user?.role);
  // UI is role-based on every device: office roles (incl. supervisors using the
  // tablet for QC) get the review queue; only the R&D desktop workspace stays
  // browser-only.
  const canSeeRD = !tablet && ['R&D', 'R&D Photographer', 'R&D Reviewer', 'Admin'].includes(user?.role);
  const canReview = ['Reviewer', 'Supervisor', 'Admin', 'R&D', 'R&D Reviewer'].includes(user?.role);
  const showComplaints = canViewComplaints(user);
  const tileCount = 2 + (canReview ? 1 : 0) + (showComplaints ? 1 : 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', minHeight: '100dvh' }}>
      <SyncBar />

      {/* Header */}
      <div className="page-header">
        <div>
          <h2 style={{ fontSize: '1.125rem' }}>{t('home.production')}</h2>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{user?.full_name} · {user?.role}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {notifications.unread > 0 && (
            <div style={{ position: 'relative' }}>
              <button className="btn btn-icon btn-ghost" onClick={() => navigate('/notifications')}>🔔</button>
              <div className="nav-badge">{notifications.unread}</div>
            </div>
          )}
          {canSeeRD && (
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/rd')}>R&D →</button>
          )}
          <button className="btn btn-ghost btn-icon" onClick={() => navigate('/settings')}>⚙</button>
        </div>
      </div>

      <div className="page-content">
        {/* Quick actions for non-photographer roles */}
        {!isPhotographer && (
          <div style={{ display: 'grid', gridTemplateColumns: tileCount > 2 ? 'repeat(auto-fit, minmax(150px, 1fr))' : '1fr 1fr', gap: 12, marginBottom: 24 }}>
            <button className="card card-hover" style={{ padding: 20, textAlign: 'left' }} onClick={() => navigate('/new-order')}>
              <div style={{ fontSize: '1.75rem', marginBottom: 8 }}>📸</div>
              <div style={{ fontWeight: 600 }}>{t('home.newsession.short')}</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 2 }}>{t('home.startphotographing')}</div>
            </button>
            <button className="card card-hover" style={{ padding: 20, textAlign: 'left' }} onClick={() => navigate('/search')}>
              <div style={{ fontSize: '1.75rem', marginBottom: 8 }}>🔍</div>
              <div style={{ fontWeight: 600 }}>{t('home.searchorders')}</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 2 }}>{t('home.findbyorder')}</div>
            </button>
            {canReview && (
              <button className="card card-hover" style={{ padding: 20, textAlign: 'left' }} onClick={() => navigate('/review')}>
                <div style={{ fontSize: '1.75rem', marginBottom: 8 }}>✅</div>
                <div style={{ fontWeight: 600 }}>{t('home.reviewqueue')}</div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 2 }}>{t('home.reviewqueue.desc')}</div>
              </button>
            )}
            {showComplaints && (
              <button className="card card-hover" style={{ padding: 20, textAlign: 'left' }} onClick={() => navigate('/complaints')}>
                <div style={{ fontSize: '1.75rem', marginBottom: 8 }}>📝</div>
                <div style={{ fontWeight: 600 }}>{t('home.complaints')}</div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 2 }}>{t('home.complaints.desc')}</div>
              </button>
            )}
          </div>
        )}

        {/* Current user actions if photographer — no account switching: a photographer
            stays on their own account and must log out to switch (CR8TIF/Lieke 2026-08-10). */}
        {isPhotographer && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button className="btn btn-primary btn-full btn-xl" onClick={() => navigate('/new-order')}>
              📸 {t('home.newsession')}
            </button>
            <button className="btn btn-secondary btn-full btn-lg" onClick={() => navigate('/my-orders')}>
              📋 {t('home.myorders')}
            </button>
            {showComplaints && (
              <button className="btn btn-secondary btn-full btn-lg" onClick={() => navigate('/complaints')}>
                📝 {t('home.complaints')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <BottomNav active="home" notifications={notifications.unread} />
    </div>
  );
}

export function BottomNav({ active, notifications = 0 }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();

  // Capture-style nav: photographer accounts get My Orders. Office roles get the
  // Review queue — on every device, including the tablet (supervisor QC).
  const captureNav = ['Photographer', 'R&D Photographer'].includes(user?.role);
  const canReview = ['Reviewer', 'Supervisor', 'Admin', 'R&D', 'R&D Reviewer'].includes(user?.role);
  const showComplaints = canViewComplaints(user);

  const items = [
    { id: 'home', icon: '🏠', label: t('nav.home'), path: '/' },
    { id: 'new', icon: '📸', label: t('nav.new'), path: '/new-order' },
    ...(captureNav ? [
      { id: 'my-orders', icon: '📋', label: t('nav.myorders'), path: '/my-orders' },
    ] : canReview ? [
      { id: 'review', icon: '✅', label: t('nav.review'), path: '/review' },
    ] : []),
    ...(showComplaints ? [
      { id: 'complaints', icon: '📝', label: t('nav.complaints'), path: '/complaints' },
    ] : []),
    { id: 'search', icon: '🔍', label: t('nav.search'), path: '/search' },
    { id: 'settings', icon: '⚙', label: t('nav.settings'), path: '/settings' },
  ];

  return (
    <div className="bottom-nav">
      {items.map(item => (
        <div key={item.id} className={`nav-item ${active === item.id ? 'active' : ''}`}
          onClick={() => navigate(item.path)}>
          <div className="nav-item-icon" style={{ position: 'relative' }}>
            {item.icon}
            {item.id === 'home' && notifications > 0 && (
              <div className="nav-badge" style={{ top: -4, right: -8 }}>{notifications}</div>
            )}
          </div>
          <div className="nav-item-label">{item.label}</div>
        </div>
      ))}
    </div>
  );
}
